import { FC } from 'react';
import { Box, Flex, Image, Text } from 'roo-ui/components';
import { tick } from 'roo-ui/assets';
import { DetailsBooking } from 'types';
import * as track from 'lib/gaTracking/crossPageEvents';
import * as trackBookingDetails from 'lib/gaTracking/bookingDetailsPageEvents';
import { getBookingCancellationQuotePageLink } from 'lib/links';
import { useRoutableModal } from 'lib/hooks/useRoutableModal';
import { ShowOnLargeScreen } from 'components/Layout';
import { Link, WideLink } from 'components/Typography';
import { FormattedDate } from 'components/Formatters';
import CancellationPolicyModal from 'components/Modals/CancellationPolicyModal';
import { useBookingDetailsPageContext } from '../../contextProvider/BookingDetailsPageContextProvider';

const getCancellationPolicyTitle = (booking: DetailsBooking) => {
  if (!booking.reservation.cancellationPolicy?.activeCancellationWindow?.isNonRefundable) {
    return 'Your booking has a flexible cancellation policy';
  } else {
    return 'Your booking is non-refundable';
  }
};

const getCancellationRefundLabel = (booking: DetailsBooking) => {
  if (booking.reservation.cancellationPolicy?.activeCancellationWindow?.isFullyRefundable) {
    return 'full refund';
  } else if (booking.reservation.cancellationPolicy?.activeCancellationWindow?.isPartiallyRefundable) {
    return 'partial refund';
  } else {
    return '';
  }
};

const CancellationInfo: FC<{ booking: DetailsBooking }> = ({ booking }) => {
  const { isNonRefundable, endTime = '' } = booking.reservation.cancellationPolicy?.activeCancellationWindow || {};
  const { RoutableModalWrapper, ...modal } = useRoutableModal('cancellation-policy', {
    onCloseIteraction: track.cancellationPolicyPopUpCloseButtonSelected,
  });
  const {
    modals: { adyenMigrationActionableModal },
  } = useBookingDetailsPageContext();

  return (
    <Box data-testid="cancellation-policy-info" p={5}>
      <Flex justifyContent="space-between" alignItems={['left', 'center']} flexDirection={['column', 'row']}>
        <Flex flexDirection={['column', 'row']}>
          <ShowOnLargeScreen>{!isNonRefundable && <Image src={tick} alt="Refundable cancellation" pr={4} mb={[2, 0]} />}</ShowOnLargeScreen>
          <Box pr={4}>
            <Text
              data-testid="cancellation-policy-title"
              fontSize="lg"
              color={isNonRefundable ? 'grey.charcoal' : 'green'}
              fontWeight="bold"
              as="div"
            >
              {getCancellationPolicyTitle(booking)}
            </Text>
            <Text data-testid="cancellation-policy-description" fontSize="sm" color="emperor" as="div">
              {!isNonRefundable && (
                <>
                  Cancel before <FormattedDate date={endTime} /> for a {getCancellationRefundLabel(booking)}.{' '}
                </>
              )}
              <Link
                data-testid="cancellation-policy-learn-more-link"
                color="emperor"
                underline
                to={modal.path}
                onClick={track.cancellationPolicyPopUpLinkSelected}
              >
                {isNonRefundable ? 'View hotel cancellation policy' : 'Learn more'}
              </Link>
            </Text>
          </Box>
        </Flex>
        {booking.flags.isCancelableInApp && !booking.flags.isFinalising && (
          <WideLink
            data-testid="cancellation-policy-option-link"
            to={getBookingCancellationQuotePageLink(booking.id)}
            onClick={(event: any) => {
              trackBookingDetails.cancellationOptionButton('Policy');
              if (adyenMigrationActionableModal.isActiveModal) {
                adyenMigrationActionableModal.openModal();
                event.preventDefault();
              }
            }}
            variant="primary"
            mt={[4, 0]}
            mr={[0, 2]}
          >
            VIEW CANCELLATION OPTIONS
          </WideLink>
        )}
      </Flex>
      <RoutableModalWrapper>
        <CancellationPolicyModal {...modal.props} rawDescription={booking.reservation.cancellationPolicy?.description ?? ''} />
      </RoutableModalWrapper>
    </Box>
  );
};

export default CancellationInfo;
